import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Calendar, MessageSquare, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_simple-realty-hub/artifacts/gydjscwr_attachment_1.jpeg";
const CALENDLY_URL = "https://calendly.com/seantherealtor/15min";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Buy', path: '/buyers' },
  { name: 'Sell', path: '/sellers' },
  { name: 'Invest', path: '/investors' },
  { name: 'Financing', path: '/financing' },
  { name: 'Open Houses', path: '/open-houses' },
  { name: 'Our Team', path: '/team' },
  { name: 'Contact', path: '/contact' },
];

const moreLinks = [
  { name: 'Property Mgmt', path: '/property-management' },
  { name: 'Join Our Team', path: '/join' },
  { name: 'Notary', path: '/notary' },
  { name: 'Resources', path: '/resources' },
];

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Simple Realty & Investments™" 
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center space-x-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link ${isActive(link.path) ? 'text-[#D4AF37]' : ''}`}
                data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            {/* More dropdown */}
            <div className="relative" onMouseEnter={() => setMoreOpen(true)} onMouseLeave={() => setMoreOpen(false)}>
              <button className="nav-link flex items-center" data-testid="nav-more-btn">
                More <ChevronDown className="w-3 h-3 ml-1" />
              </button>
              {moreOpen && (
                <div className="absolute top-full right-0 mt-1 bg-[#111111] border border-[#D4AF37]/20 rounded-lg shadow-xl min-w-[180px] py-2" data-testid="more-dropdown">
                  {moreLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`block px-4 py-2 text-sm transition-colors ${isActive(link.path) ? 'text-[#D4AF37] bg-[#D4AF37]/5' : 'text-[#F5F5F0] hover:bg-[#1A1A1A] hover:text-[#D4AF37]'}`}
                      onClick={() => setMoreOpen(false)}
                      data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {link.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden xl:flex items-center space-x-2">
            <a href="tel:7863560878" data-testid="header-call-btn">
              <Button variant="outline" className="btn-outline-gold text-sm">
                <Phone className="w-4 h-4 mr-1" />
                Call
              </Button>
            </a>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" data-testid="header-calendly-btn">
              <Button className="btn-gold text-sm font-medium">
                <Calendar className="w-4 h-4 mr-1" />
                Book Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="xl:hidden p-2 text-[#F5F5F0]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-[#0a0a0a] border-t border-[#D4AF37]/20 max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <nav className="px-4 py-4 space-y-1">
            {[...navLinks, ...moreLinks].map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-3 px-4 rounded-lg transition-colors ${
                  isActive(link.path)
                    ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                    : 'text-[#F5F5F0] hover:bg-[#1A1A1A]'
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 space-y-3 border-t border-[#D4AF37]/10 mt-2">
              <a href="tel:7863560878" className="block">
                <Button className="btn-gold w-full">
                  <Phone className="w-4 h-4 mr-2" /> Call (786) 356-0878
                </Button>
              </a>
              <a href="sms:7863560878" className="block">
                <Button variant="outline" className="btn-outline-gold w-full">
                  <MessageSquare className="w-4 h-4 mr-2" /> Text Us
                </Button>
              </a>
              <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="block">
                <Button variant="outline" className="btn-outline-gold w-full">
                  <Calendar className="w-4 h-4 mr-2" /> Book Appointment
                </Button>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};
